import { DOCUMENT, Injectable, NgZone, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Params } from '@angular/router';

import { FixtureBase } from '@cds/betting-offer';
import { YahooOpenIdConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { NavigationHelper } from '@frontend/sports/navigation/core/feature/helpers';
import { UrlParam } from '@frontend/sports/navigation/core/feature/url-builder';
import {
    FavouritesDashboardUrlParam,
    LiveUrlParam,
    MarketUrlParam,
    MyBetsUrlParam,
    SportUrlParam,
    UrlHelperService,
} from '@frontend/sports/navigation/core/feature/url-helper';
import { RacingUrlParam } from '@frontend/sports/racing/feature/models';
import {
    CookieService,
    GoToOptions,
    LoginProvider,
    MenuAction,
    MenuActionsService,
    NativeAppService,
    NavigationService,
    WINDOW,
} from '@frontend/vanilla/core';
import { LoginProvidersService } from '@frontend/vanilla/shared/login-providers';
import { Subject, concatMap, distinctUntilKeyChanged, tap, timer, windowWhen } from 'rxjs';

export enum YahooOpenIdTriggerOption {
    BetSlip = 'betslip',
}

export interface GoToLink {
    url: string;
    options: GoToOptions;
}

@Injectable({ providedIn: 'root' })
export class RedirectHelperService {
    private navigationHelper = inject(NavigationHelper);
    private urlHelper = inject(UrlHelperService);
    private menuActionsService = inject(MenuActionsService);
    private loginWithProvidersService = inject(LoginProvidersService);
    private cookieService = inject(CookieService);
    private yahooOpenidconfig = inject(YahooOpenIdConfig);
    private nativeAppService = inject(NativeAppService);

    private readonly navigationSubject$ = new Subject<GoToLink>();
    private readonly _window = inject(WINDOW);
    private readonly _doc = inject(DOCUMENT);

    constructor() {
        const navigation = inject(NavigationService);
        const ngZone = inject(NgZone);

        ngZone.runOutsideAngular(() =>
            this.navigationSubject$
                .pipe(
                    windowWhen(() => timer(300)),
                    concatMap((obs) => obs.pipe(distinctUntilKeyChanged('url'))),
                    tap((redirect: GoToLink) => navigation.goTo(redirect.url, redirect.options)),
                    takeUntilDestroyed(),
                )
                .subscribe(),
        );
    }

    goToLiveSport(options: { sport: UrlParam; region?: UrlParam; ignoreBrowserHistory?: boolean; isEsport?: boolean }): void {
        this.goToPage(
            this.urlHelper.getLiveSportUrl({ sport: options.sport, region: options.region, isEsport: options.isEsport }),
            options.ignoreBrowserHistory,
        );
    }

    goToLive(tab: LiveUrlParam = LiveUrlParam.Highlights, tracking?: string): void {
        this.goToPage(this.urlHelper.getLiveUrl(tab, tracking));
    }

    goToSport(sport: UrlParam, context?: SportUrlParam, subContext?: SportUrlParam.Competitions, ignoreBrowserHistory?: boolean): void;
    goToSport(sport: UrlParam, region?: UrlParam, league?: UrlParam, ignoreBrowserHistory?: boolean): void;
    goToSport(
        sport: UrlParam,
        regionOrContext?: UrlParam | SportUrlParam,
        leagueOrSubContext?: UrlParam | SportUrlParam.Competitions,
        ignoreBrowserHistory = false,
    ): void {
        let url: string;

        url =
            !regionOrContext || typeof regionOrContext === 'string'
                ? this.urlHelper.getSportUrl(sport, regionOrContext as SportUrlParam, leagueOrSubContext as SportUrlParam.Competitions)
                : this.urlHelper.getSportUrl(sport, SportUrlParam.Betting, regionOrContext, leagueOrSubContext as UrlParam);

        this.goToPage(url, ignoreBrowserHistory);
    }

    goToVirtualSport(sport: UrlParam, competition?: UrlParam, event?: string): void {
        this.goToPage(this.urlHelper.getVirtualSportUrl(sport, competition, event));
    }

    goToEvent(event: UrlParam, market?: MarketUrlParam): void {
        this.goToPage(this.urlHelper.getEventUrl(event, market));
    }

    goToLogin(options?: {
        forceReload?: boolean;
        appendReferrer?: boolean | string;
        referrerNeedsLoggedInUser?: boolean;
        useYahooLogin?: boolean;
    }): void {
        const returnUrl = typeof options?.appendReferrer === 'string' ? options.appendReferrer : '';
        if (options?.useYahooLogin && this.isYahooOpenIdFlow()) {
            this.loginWithProvidersService.urlAuth({
                provider: LoginProvider.YAHOO,
                redirectQueryParams: { trigger: YahooOpenIdTriggerOption.BetSlip },
            });
        } else {
            this.menuActionsService.invoke(MenuAction.GOTO_LOGIN, '', [undefined, undefined, { returnUrl }]);
        }
    }

    goToHorseRacing(page: RacingUrlParam): void;
    goToHorseRacing(meeting: UrlParam, filterOrRace?: RacingUrlParam | UrlParam): void;
    goToHorseRacing(param: RacingUrlParam | UrlParam, filterOrRace?: RacingUrlParam | UrlParam): void {
        if (typeof param === 'string') {
            this.goToPage(this.urlHelper.getRaceUrl(SportConstant.Horses, param));
        } else if (param) {
            this.goToPage(this.urlHelper.getRaceUrl(SportConstant.Horses, param, filterOrRace));
        }
    }

    goToFixtureByFixture(fixture: Pick<FixtureBase, 'id' | 'name' | 'sport' | 'fixtureType' | 'meeting'>): void {
        this.goToPage(this.urlHelper.getFixtureUrlByFixture(fixture));
    }

    goToFixture(sport: UrlParam, fixture: UrlParam, prefix?: string): void {
        this.goToPage(this.urlHelper.getFixtureUrl(sport, fixture, prefix));
    }

    goToFavouriteWithUrlParam(urlParam: FavouritesDashboardUrlParam): void {
        this.goToPage(this.urlHelper.getFavouriteUrlByUrlParam(urlParam));
    }

    goHome(ignoreBrowserHistory?: boolean, isBackNavigation = false): void {
        this.goToPage(this.urlHelper.getHomeUrl(), ignoreBrowserHistory ?? true, isBackNavigation);
    }

    goToMyBets(route?: MyBetsUrlParam, trackingParams?: Params, ignoreBrowserHistory = true): void {
        this.goToPage(this.urlHelper.getMyBetsUrl(route, trackingParams), ignoreBrowserHistory);
    }

    goToPage(url?: string, ignoreBrowserHistory = false, isBackNavigation = false): void {
        if (!url) {
            return;
        }

        this.navigationSubject$.next({ url, options: { replace: ignoreBrowserHistory, isBackNavigation } });
    }

    goToRacing(sportId: SportConstant, param: RacingUrlParam | UrlParam, filterOrRace?: RacingUrlParam | UrlParam): void {
        this.goToPage(this.urlHelper.getRaceUrl(sportId, param, filterOrRace));
    }

    goBack(url?: string): void {
        if (url) {
            this.goToPage(url, undefined, true);

            return;
        }

        if (this.navigationHelper.forceHomePage() && !this.userIsComingFromPortalPromotions) {
            this.goToPage('/', undefined, true);

            return;
        }

        this._window.history.back();
    }

    reloadPage(): void {
        this._window.location.reload();
    }

    private isYahooOpenIdFlow(): boolean {
        const nativeAppValue = this.yahooOpenidconfig.nativeAppCookie || '';
        const loginHint = !!this.cookieService.get('login_hint');
        const idTokenHint = !!this.cookieService.get('id_token_hint');

        return this.yahooOpenidconfig.enableOnBetPlacement && this.nativeAppService.applicationName === nativeAppValue && (loginHint || idTokenHint);
    }

    private get userIsComingFromPortalPromotions(): boolean {
        const referrer = this._doc.referrer;

        return referrer.includes('promotions');
    }
}
